if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        // AI1 gallery-interaction fix (Aug 2026).
        // ROOT CAUSE: stock Dawn binds the thumbnail click handlers in this
        // constructor, but the FIRST thing it does is
        //   viewer.addEventListener('slideChanged', debounce(...))
        // `debounce` comes from global.js. On this store the <media-gallery>
        // element is upgraded before global.js has defined `debounce`, so that
        // line THROWS and the constructor aborts BEFORE the thumbnail binding
        // ever runs -> clicking a thumbnail did nothing.
        // FIX: (1) attach the thumbnail handler FIRST and via delegation on the
        // stable thumbnails container (also robust to timing/re-render), and
        // (2) guard the debounce/slideChanged line so a missing global can no
        // longer abort init. Behaviour is otherwise identical to Dawn.
        const init = () => {
          if (this.__ai1Bound) return;
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
          this.mql = window.matchMedia('(min-width: 750px)');
          if (!this.elements.thumbnails) return;
          this.__ai1Bound = true;
          this.ai1InitFx();

          this.elements.thumbnails.addEventListener('click', (event) => {
            const mediaToSwitch = event.target.closest('[data-target]');
            if (mediaToSwitch && this.elements.thumbnails.contains(mediaToSwitch)) {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            }
          });

          try {
            const slideHandler =
              typeof debounce === 'function'
                ? debounce(this.onSlideChanged.bind(this), 500)
                : this.onSlideChanged.bind(this);
            this.elements.viewer.addEventListener('slideChanged', slideHandler);
          } catch (e) {}

          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        };
        init();
        if (!this.__ai1Bound) {
          document.addEventListener('DOMContentLoaded', init, { once: true });
          window.addEventListener('load', init, { once: true });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // AI1 layered image transitions (Oct 2026).
      // Dawn's activation logic is kept verbatim in ai1NativeSetActiveMedia().
      // setActiveMedia() wraps it: it waits until the incoming image is loaded,
      // snapshots the outgoing image as a temporary non-interactive layer, lets
      // Dawn switch the active slide, then animates (Web Animations API).
      //   colour option change -> layered crossfade with depth  (480ms)
      //   other option change  -> restrained depth shift        (320ms)
      //   thumbnail selection  -> directional interleaved reveal (300ms)
      ai1InitFx() {
        this.__ai1 = { token: 0, change: null, manualAt: 0, cur: null };
        this.__ai1OnChange = (event) => {
          const target = event.target;
          if (!target || !target.closest || !target.closest('variant-selects')) return;
          const scope = this.closest('product-info');
          if (scope && !scope.contains(target)) return;
          const label = (
            (target.closest('fieldset') && target.closest('fieldset').querySelector('legend')
              ? target.closest('fieldset').querySelector('legend').textContent
              : '') ||
            target.name ||
            ''
          ).toLowerCase();
          this.__ai1.change = { kind: /colou?r|لون/.test(label) ? 'colour' : 'option', at: performance.now() };
        };
        document.addEventListener('change', this.__ai1OnChange);
      }

      disconnectedCallback() {
        if (this.__ai1OnChange) document.removeEventListener('change', this.__ai1OnChange);
        this.ai1CancelFx();
      }

      ai1CancelFx() {
        const fx = this.__ai1;
        if (!fx || !fx.cur) return;
        fx.cur.anims.forEach((animation) => animation.cancel());
        if (fx.cur.layer) fx.cur.layer.remove();
        if (fx.cur.img) fx.cur.img.style.zIndex = '';
        fx.cur = null;
      }

      ai1ImageReady(img) {
        return new Promise((resolve) => {
          const ok = () => img.complete && img.naturalWidth > 0;
          if (ok()) return resolve(true);
          const onLoad = () => finish(true);
          const onError = () => finish(false);
          const finish = (result) => {
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            resolve(result);
          };
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onError);
          img.loading = 'eager';
          if (ok()) finish(true);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const fx = this.__ai1;
        const viewer = this.elements && this.elements.viewer;
        if (!fx || !viewer) return this.ai1NativeSetActiveMedia(mediaId, prepend);

        let kind = null;
        if (prepend) {
          const change = fx.change;
          fx.change = null;
          // a thumbnail chosen after the option change must not be overridden by the delayed variant update
          if (change && fx.manualAt > change.at) return;
          kind = change ? change.kind : null;
        } else {
          kind = 'manual';
          fx.manualAt = performance.now();
        }

        const token = ++fx.token;
        this.ai1CancelFx();

        const next = viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const prev = viewer.querySelector('[data-media-id].is-active');
        const nextImg = next && next.querySelector('.product__modal-opener--image img');
        const prevImg = prev && prev.querySelector('.product__modal-opener--image img');
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const direct = () => this.ai1NativeSetActiveMedia(mediaId, prepend);

        if (!kind || reduced || !next || !prev || next === prev || !nextImg || !prevImg) return direct();
        // manual selection only animates when slides are shown one at a time; otherwise native scrolling is the transition
        if (kind === 'manual' && getComputedStyle(next).display !== 'none') return direct();
        if (kind !== 'manual' && getComputedStyle(prev).display === 'none') return direct();

        if (kind === 'manual' && this.elements.thumbnails) {
          this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`));
        }

        const items = Array.from(viewer.querySelectorAll('[data-media-id]'));
        const direction = items.indexOf(next) >= items.indexOf(prev) ? 1 : -1;

        this.ai1ImageReady(nextImg)
          .then((loaded) => (loaded && nextImg.decode ? nextImg.decode().then(() => true, () => true) : loaded))
          .then((loaded) => {
            if (token !== fx.token) return; // superseded by a newer selection
            const layer = prevImg.cloneNode(false);
            layer.removeAttribute('id');
            layer.removeAttribute('data-media-id');
            layer.removeAttribute('loading');
            layer.removeAttribute('class');
            layer.className = prevImg.className;
            layer.alt = '';
            layer.setAttribute('aria-hidden', 'true');
            layer.style.pointerEvents = 'none';
            layer.style.zIndex = '1';

            direct();
            if (!loaded || token !== fx.token) return;
            const host = nextImg.closest('.product__media') || nextImg.parentElement;
            if (!host) return;
            host.appendChild(layer);

            const ease = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
            let inKeys;
            let outKeys;
            let duration;
            if (kind === 'colour') {
              duration = 480;
              inKeys = [{ transform: 'scale(1.05) translate3d(0, 10px, 0)' }, { transform: 'none' }];
              outKeys = [
                { opacity: 1, transform: 'none' },
                { opacity: 0, transform: 'scale(0.985) translate3d(0, -8px, 0)' },
              ];
            } else if (kind === 'option') {
              duration = 320;
              inKeys = [{ transform: 'scale(1.015)' }, { transform: 'none' }];
              outKeys = [
                { opacity: 1, transform: 'none' },
                { opacity: 0, transform: 'scale(0.992)' },
              ];
            } else {
              duration = 300;
              nextImg.style.zIndex = '2';
              inKeys = [
                { opacity: 0, transform: `translate3d(${direction * 8}%, 0, 0)` },
                { opacity: 1, transform: 'none' },
              ];
              outKeys = [
                { opacity: 1, transform: 'none' },
                { opacity: 0, transform: `translate3d(${direction * -4}%, 0, 0)` },
              ];
            }

            const anims = [
              nextImg.animate(inKeys, { duration, easing: ease }),
              layer.animate(outKeys, { duration, easing: ease, fill: 'forwards' }),
            ];
            fx.cur = { layer, img: nextImg, anims };
            Promise.all(anims.map((animation) => animation.finished)).then(
              () => {
                if (fx.cur && fx.cur.layer === layer) this.ai1CancelFx();
              },
              () => {}
            );
          });
      }

      ai1NativeSetActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
